import React from 'react';
import { SelfMonitorData, SelfMonitorSample } from '../../../types/SelfMonitor';
import { formatBytes, ingestBytesInWindow, ingestRate } from './ingest';
import { extraNum } from './sampleUtils';

interface StatTilesProps {
    data: SelfMonitorData | null;
    periodMs: number;
}

const bytesToMb = (bytes: number) => bytes / (1024 * 1024);

const latest = (samples: SelfMonitorSample[]): SelfMonitorSample | undefined =>
    samples.length > 0 ? samples[samples.length - 1] : undefined;

const formatPercent = (fraction: number | undefined): string =>
    fraction === undefined || fraction < 0 ? '--' : (fraction * 100).toFixed(1);

const formatUptime = (ms: number | undefined): string => {
    if (!ms || ms <= 0) return '--';
    const days = ms / (1000 * 60 * 60 * 24);
    if (days >= 1) return `${days.toFixed(1)}d`;
    const hours = ms / (1000 * 60 * 60);
    if (hours >= 1) return `${hours.toFixed(1)}h`;
    return `${Math.round(ms / 60000)}m`;
};

const formatJvmRestart = (sample: SelfMonitorSample | undefined): string | undefined => {
    if (!sample?.uptimems || sample.uptimems <= 0) return undefined;
    const restartedAt = new Date(sample.time - sample.uptimems);
    return `Restarted ${restartedAt.toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })}`;
};

interface Tile {
    label: string;
    value: string;
    unit?: string;
    sub?: string;
    progress?: number;
}

type GroupKey = 'jvm' | 'agent' | 'archiver';

interface TileGroup {
    key: GroupKey;
    title: string;
    tiles: Tile[];
}

// One outline colour per group, using the theme's semantic Bootstrap
// variables so the coding stays correct in dark mode too.
const GROUP_COLORS: Record<GroupKey, string> = {
    jvm: 'var(--bs-primary)',
    agent: 'var(--bs-warning)',
    archiver: 'var(--bs-success)',
};

const formatBitRate = (bytesPerSecond: number): string => {
    const bitsPerSecond = bytesPerSecond * 8;
    if (bitsPerSecond >= 1_000_000) return `${(bitsPerSecond / 1_000_000).toFixed(1)} Mbit/s`;
    return `${(bitsPerSecond / 1_000).toFixed(1)} kbit/s`;
};

const StatTiles: React.FC<StatTilesProps> = ({ data, periodMs }) => {
    const archiverSample = data ? latest(data.archiver.samples) : undefined;
    const agents = data?.agents ?? [];
    const agentLatest = agents
        .map(agent => latest(agent.samples))
        .filter((s): s is SelfMonitorSample => !!s);

    const heapUsedMb = archiverSample ? bytesToMb(archiverSample.heapused) : 0;
    const heapMaxMb = archiverSample ? bytesToMb(archiverSample.heapmax) : 0;
    const heapPct = heapMaxMb > 0 ? (heapUsedMb / heapMaxMb) * 100 : 0;

    const httpBusy = extraNum(archiverSample, 'http_threads_busy');
    const httpMax = extraNum(archiverSample, 'http_threads_max');

    const archiverPoolActive = extraNum(archiverSample, 'archiver_pool_active');
    const archiverPoolMax = extraNum(archiverSample, 'archiver_pool_max');
    const archiverPoolWaiting = extraNum(archiverSample, 'archiver_pool_waiting');

    const archiverSchedulerActive = extraNum(archiverSample, 'archiver_scheduler_active');
    const archiverSchedulerPoolSize = extraNum(archiverSample, 'archiver_scheduler_pool_size');

    const agentPoolActive = agentLatest.reduce((sum, s) => sum + extraNum(s, 'agent_pool_active'), 0);
    const agentPoolMax = agentLatest.reduce((sum, s) => sum + extraNum(s, 'agent_pool_max'), 0);
    // active/idle are a point-in-time read of pools that are normally only
    // open for the few milliseconds a sensor query takes, so they will
    // usually show 0 between samples even when sensors are running - the
    // borrowed count (a running total since the last sample) is proof of
    // actual use that does not depend on the sample landing mid-query.
    const agentPoolBorrowed = agentLatest.reduce((sum, s) => sum + extraNum(s, 'agent_pool_borrowed'), 0);

    // Same point-in-time caveat applies to the sensor executors themselves -
    // sum the three schedulers' active count/pool size across agents, and
    // use the completed count (a per-sample delta - see
    // AgentSelfMonitor.addExecutorStats) as the liveness signal an
    // instantaneous "active" count can miss.
    const agentThreadsActive = agentLatest.reduce((sum, s) =>
        sum + extraNum(s, 'agent_activity_active') + extraNum(s, 'agent_batch_active') + extraNum(s, 'agent_host_active'), 0);
    const agentThreadsPoolSize = agentLatest.reduce((sum, s) =>
        sum + extraNum(s, 'agent_activity_pool_size') + extraNum(s, 'agent_batch_pool_size') + extraNum(s, 'agent_host_pool_size'), 0);
    const agentTasksCompleted = agentLatest.reduce((sum, s) =>
        sum + extraNum(s, 'agent_activity_completed') + extraNum(s, 'agent_batch_completed') + extraNum(s, 'agent_host_completed'), 0);

    // Datasources/hosts configured per agent - not a health number like the
    // rest of this group, but the one figure that answers "how much is
    // actually being watched".
    const agentTargetsMonitored = agentLatest.reduce((sum, s) =>
        sum + extraNum(s, 'agent_datasource_count') + extraNum(s, 'agent_host_count'), 0);

    // Counted on the archiver as agent uploads arrive (see
    // IngestCountingFilter), so it covers every agent, not just
    // those pushing self-monitor samples. Resets when the archiver restarts.
    const archiverSamples = data?.archiver.samples ?? [];
    const ingested = formatBytes(ingestBytesInWindow(archiverSamples, periodMs));
    const ingestedSinceStart = formatBytes(extraNum(archiverSample, 'ingest_bytes_total'));
    const ingestRateNow = formatBitRate(ingestRate(archiverSamples));

    const groups: TileGroup[] = [
        {
            key: 'jvm',
            title: 'Archiver JVM',
            tiles: [
                {
                    label: 'JVM Heap',
                    value: heapUsedMb.toFixed(0),
                    unit: 'MB',
                    sub: `Max: ${heapMaxMb.toFixed(0)} MB`,
                    progress: heapPct,
                },
                {
                    label: 'GC Pause Time',
                    value: ((archiverSample?.gctimems ?? 0) / 1000).toFixed(1),
                    unit: 's',
                    sub: `${archiverSample?.gccount ?? 0} collections`,
                },
                {
                    label: 'CPU Usage',
                    value: formatPercent(archiverSample?.processcpu),
                    unit: '%',
                    sub: `${archiverSample?.cores ?? 0} cores`,
                    progress: (archiverSample?.processcpu ?? 0) * 100,
                },
                {
                    label: 'JVM Uptime',
                    value: formatUptime(archiverSample?.uptimems),
                    sub: formatJvmRestart(archiverSample),
                },
            ],
        },
        {
            key: 'agent',
            title: 'Agent',
            tiles: [
                {
                    label: 'Agent DB Conns',
                    value: `${agentPoolActive}`,
                    unit: 'active',
                    sub: agentPoolBorrowed > 0
                        ? `Limit: ${agentPoolMax || '--'} · ${agentPoolBorrowed} borrowed recently`
                        : `Limit: ${agentPoolMax || '--'}`,
                    progress: agentPoolMax > 0 ? (agentPoolActive / agentPoolMax) * 100 : 0,
                },
                {
                    label: 'Agent Threads',
                    value: `${agentThreadsActive}`,
                    unit: 'active',
                    sub: agentTasksCompleted > 0
                        ? `Pool size: ${agentThreadsPoolSize} · ${agentTasksCompleted} tasks completed recently`
                        : `Pool size: ${agentThreadsPoolSize}`,
                },
                {
                    label: 'Data Ingested',
                    value: ingested.value,
                    unit: ingested.unit,
                    sub: `Since archiver start: ${ingestedSinceStart.value} ${ingestedSinceStart.unit} (${ingestRateNow})`,
                },
                {
                    label: 'Targets Monitored',
                    value: `${agentTargetsMonitored}`,
                    sub: agents.length > 0
                        ? `Across ${agents.length} agent${agents.length === 1 ? '' : 's'}`
                        : undefined,
                },
            ],
        },
        {
            key: 'archiver',
            title: 'Archiver and API',
            tiles: [
                {
                    label: 'Archiver DB Conns',
                    value: `${archiverPoolActive}`,
                    unit: 'active',
                    sub: `Limit: ${archiverPoolMax || '--'}`,
                    progress: archiverPoolMax > 0 ? (archiverPoolActive / archiverPoolMax) * 100 : 0,
                },
                {
                    label: 'Archiver Threads',
                    value: `${archiverSchedulerActive}`,
                    unit: 'active',
                    sub: `Pool size: ${archiverSchedulerPoolSize}`,
                },
                {
                    label: 'HTTP Threads',
                    value: `${httpBusy}`,
                    unit: 'busy',
                    sub: `Limit: ${httpMax || '--'}`,
                    progress: httpMax > 0 ? (httpBusy / httpMax) * 100 : 0,
                },
                {
                    label: 'Repository Pool Waiting',
                    value: `${archiverPoolWaiting}`,
                    unit: 'waiting',
                    sub: archiverPoolWaiting > 0 ? 'Repository pool may be undersized' : undefined,
                },
            ],
        },
    ];

    const [jvmGroup, agentGroup, archiverGroup] = groups;

    // Flattened back into a plain 6-across, 2-row grid (no group card
    // wrappers) - JVM's 4 tiles split top-left/bottom-left the same way
    // they did in the boxed layout, so the two rows read the same as
    // before: [JVM x2, Agent x4] then [JVM x2, Archiver x4]. Grouping is
    // now shown with a coloured outline per tile instead of a shared box.
    const orderedTiles: { tile: Tile; group: GroupKey }[] = [
        ...jvmGroup.tiles.slice(0, 2).map(tile => ({ tile, group: jvmGroup.key })),
        ...agentGroup.tiles.map(tile => ({ tile, group: agentGroup.key })),
        ...jvmGroup.tiles.slice(2, 4).map(tile => ({ tile, group: jvmGroup.key })),
        ...archiverGroup.tiles.map(tile => ({ tile, group: archiverGroup.key })),
    ];

    // `d-flex` on the col + height:100% on the card (see style.scss) is the
    // standard Bootstrap equal-height-card recipe - without it, a taller
    // sibling in the same row (e.g. a wrapped two-line label) stretches the
    // .col but leaves the shorter card sitting at its natural height inside
    // that extra space, so values in the same row stop lining up.
    const renderTile = (tile: Tile, group: GroupKey) => (
        <div className="col d-flex" key={tile.label}>
            <div className="card self-monitor-tile">
                <div className="tile-label" style={{ color: GROUP_COLORS[group] }}>{tile.label.toUpperCase()}</div>
                <div className="tile-value">
                    {tile.value}
                    {tile.unit && <span className="tile-unit">{tile.unit}</span>}
                </div>
                {tile.sub && <div className="tile-sub">{tile.sub}</div>}
                {tile.progress !== undefined && (
                    <div className="tile-progress">
                        <div
                            className="tile-progress-bar"
                            style={{ width: `${Math.min(100, Math.max(0, tile.progress))}%` }}
                        />
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className="self-monitor-tiles row row-cols-2 row-cols-sm-3 row-cols-lg-6 gy-2">
            {orderedTiles.map(({ tile, group }) => renderTile(tile, group))}
        </div>
    );
};

export default StatTiles;
